import { TypedDocumentNode, gql } from '@apollo/client';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, HeadlineS, LabelL } from '@sorare/core/src/atoms/typography';
import { MUIBadge } from '@sorare/core/src/atoms/ui/MUIBadge';
import { ANY_SPORT_MY_CLUB_CHALLENGES } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { Link } from '@sorare/core/src/routing/Link';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { LevelBar } from 'components/user/progression/LevelBar';

import {
  ChallengesHomeEntry_managerProgression,
  ChallengesHomeEntry_user,
} from './__generated__/index.graphql';

const StyledLink = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: var(--unit);
`;

const FirstRow = styled(Horizontal).attrs({ gap: 0.5 })`
  & > *:nth-child(2) {
    flex: 1;
  }
`;

const SeeMoreArrow = styled.div`
  @media ${tabletAndAbove} {
    display: none;
  }
`;

const SeeMoreLabel = styled(LabelL)`
  display: none;
  @media ${tabletAndAbove} {
    display: block;
  }
`;

const StyledLevelBar = styled(LevelBar)`
  min-width: auto;
  min-height: var(--unit);
`;

type Props = {
  managerProgression: ChallengesHomeEntry_managerProgression | null;
  user: Nullable<ChallengesHomeEntry_user>;
  nbChallengesToClaim: number;
};

export const ChallengesHomeEntry = ({
  managerProgression,
  user,
  nbChallengesToClaim,
}: Props) => {
  const { generateSportContextPath } = useSportContext();

  if (!managerProgression) {
    return null;
  }

  const managerIdLink = generateSportContextPath(ANY_SPORT_MY_CLUB_CHALLENGES, {
    params: { slug: user?.slug || '' },
  });

  return (
    <StyledLink to={managerIdLink}>
      <FirstRow>
        <BodyL as="p">
          <FormattedMessage
            id="ChallengesHomeEntry.level"
            defaultMessage="Level"
          />
        </BodyL>
        <HeadlineS as="p">{managerProgression.levelReached}</HeadlineS>
        <SeeMoreArrow>
          <FontAwesomeIcon icon={faChevronRight} />
        </SeeMoreArrow>

        {nbChallengesToClaim > 0 ? (
          <MUIBadge badgeContent={nbChallengesToClaim} />
        ) : (
          <SeeMoreLabel as="p" color="var(--c-neutral-600)" bold>
            <FormattedMessage
              id="ChallengesHomeEntry.seeMore"
              defaultMessage="See more"
            />
          </SeeMoreLabel>
        )}
      </FirstRow>
      <StyledLevelBar managerProgression={managerProgression} hideLevels />
    </StyledLink>
  );
};

ChallengesHomeEntry.fragments = {
  managerProgression: gql`
    fragment ChallengesHomeEntry_managerProgression on ManagerProgression {
      id
      ...LevelBar_ManagerProgression
    }
    ${LevelBar.fragments.managerProgression}
  ` as TypedDocumentNode<ChallengesHomeEntry_managerProgression>,
  user: gql`
    fragment ChallengesHomeEntry_user on PublicUserInfoInterface {
      slug
    }
  ` as TypedDocumentNode<ChallengesHomeEntry_user>,
};
